import { ChangeEvent, useState } from 'react';
import { flowState } from '../../pages/AddPrize/AddPrizePage';
import { Box, Grid, Typography } from '@mui/material';
import TooltipText from '../../components/inputComponents/TooltipText';
import { RadioButton } from '../../components/inputComponents/RadioButton';
import { InputNumberSpinner } from '../../components/inputComponents/InputNumberSpinner';
import { deliveryType } from '../../constants/lists';
import { InputComponent } from '../inputComponents/InputComponent';
import { MultipleSelectOptions } from '../selectComponents/MultipleSelectOptions';
import { AddToListModal } from '../modals/AddToListModal';
import * as yup from 'yup';
import { getIn } from 'formik';
import { CheckboxComponent } from '../inputComponents/CheckboxComponent';
import { DynamicVoucherSection } from './DynamicVoucherSection';
import { BarcodeTypeComponent } from './BarcodeTypeComponent';

const tags = [1];

interface props {
    isEdit?: boolean;
}

const tagsValidationSchema = yup.object({
    tag: yup
        .string()
        .matches(/.*/)
        .test(
            'Max length',
            'Max length is 250 characters',
            (val: any) => val && val.toString().length <= 250
        )
        .required('Field is required'),
});

function AdditionalDetails({ isEdit, formik }: flowState & props) {
    const [open, setOpen] = useState(false);
    const isCNG: boolean = formik.values?.tempParams?.mechanic === 'c&g' || false;
    const isDynamicGeneration: boolean = formik.values.dynamic_gen === 'true';

    const handleOpen = () => setOpen(true);

    const handleCheckVoucherRedemption = (e: ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('redeem_restrict', e.target.checked ? true : false);
    };

    const handleDynamicVoucherGeneration = (e: ChangeEvent<HTMLInputElement>) => {
        formik.setFieldValue('dynamic_gen', e.target.checked ? true : false);
    };

    return (
        <Box className='prize-box-main'>
            <Typography variant='h4' gutterBottom>
                Additional Details
            </Typography>
            <Typography variant='body1' gutterBottom>
                Specify additional details for this prize
            </Typography>
            <Box className='additional-details-content'>
                    <TooltipText
                        dataTestId="voucher-distribution"
                        styleName="prize-tooltip-text"
                        text="Voucher Distribution"
                        textVariant="body1"
                        tooltip="No can be selected for digital prizes that don't require vouchers as proof of winning, e.g. uploaded assets or hidden URLs."
                    />
                <RadioButton
                    disabled={isEdit}
                    labelVariant='body2'
                    formik={formik}
                    values={[
                        { value: 'true', label: 'Yes' },
                        { value: 'false', label: 'No' },
                    ]}
                    radioGroupName={'voucher_dist'}
                    onChange={(e) => {
                        const value = e.target.value;
                        formik.setFieldValue('voucher_dist', value);
                        formik.setFieldValue('dynamic_gen', '');
                    }}
                />
                <RadioButton
                    disabled={isEdit}
                    labelVariant="body2"
                    formik={formik}
                    values={[
                        { value: 'true', label: 'Dynamic' },
                    ]}
                    radioGroupName="dynamic_gen"
                    onChange={(e) => {
                        formik.setFieldValue('dynamic_gen', 'true');
                        formik.setFieldValue('voucher_dist', '');
                    }}
                    tooltipMap={{
                        true: (
                            <TooltipText
                                dataTestId="dynamic-voucher-tooltip"
                                styleName="prize-tooltip-text"
                                text="Dynamic Voucher Generation"
                                textVariant="body1"
                                tooltip="When selected, this prize can be claimed without generating or assigning vouchers. Optionally, you can define a custom prefix or suffix for the generated unique ID."
                                showText={false}
                            />
                    )}}
                />
                {formik.values.voucher_dist === 'true' && (
                    <Grid
                        container
                        direction='row'
                        justifyContent='flex-start'
                        alignItems='flex-start'
                        spacing={2}
                    >
                        <Grid item xs={12} container alignItems="center" >
                            <CheckboxComponent
                                customBoxClass='redeem_restrict-checkbox'
                                fieldName={'redeem_restrict'}
                                label='Voucher Redemption Restriction'
                                customAction={handleCheckVoucherRedemption}
                            />
                            <Grid item>
                                <TooltipText
                                    dataTestId='voucher-redemption-restriction-tooltip'
                                    styleName='prize-tooltip-text'
                                    text=''
                                    textVariant='body1'
                                    tooltip='Select this option if vouchers can be redeemed only by consumers who claimed them.'
                                />
                            </Grid>
                        </Grid>
                        <Grid item xs={10}>
                            <BarcodeTypeComponent formik={formik} />
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant='body1'>Delivery Type</Typography>
                            <RadioButton
                                labelVariant='body2'
                                formik={formik}
                                values={deliveryType}
                                radioGroupName={'delivery_type'}
                            />
                        </Grid>
                    </Grid>
                )}
                {isCNG && (
                    <InputComponent
                            inputClassName='input-space'
                            formik={formik}
                            inputValue={getIn(formik.values, 'min_age')}
                            formikLabel={'min_age'}
                            title='Minimum age'
                            titleVariant='body1'
                        />
                )}
                {isDynamicGeneration && (
                    <>
                        <DynamicVoucherSection
                            formik={formik}
                            onToggleVoucherGeneration={handleDynamicVoucherGeneration}
                        />
                        <BarcodeTypeComponent formik={formik} />
                    </>
                )}
                {(formik.values.voucher_dist === 'false' || isDynamicGeneration) && (
                    <Box sx={{ marginTop: 2 }}>
                        <InputComponent
                            inputClassName='input-space'
                            formik={formik}
                            inputValue={getIn(formik.values, 'total_amount')}
                            formikLabel={'total_amount'}
                            title='Total Prize Quantity'
                            titleVariant='body1'
                        />
                        <InputComponent
                            inputClassName='input-space'
                            formik={formik}
                            inputValue={getIn(formik.values, 'total_available')}
                            formikLabel={'total_available'}
                            title='Available Prize Quantity'
                            titleVariant='body1'
                        />
                    </Box>
                )}
                <TooltipText
                    dataTestId='final-state-tooltop'
                    styleName='prize-tooltip-text'
                    text='Final State'
                    textVariant='body1'
                    tooltip="Indicate the final state of this prize.
                        If winners still have to redeem the prize on partner's website or physical location which are integrated with NGPS backend to change prize status, select 'Claimed' option. This informs NGPS about the actual redemption event and can be useful for tracking and analytics.
                        If you don't have a programmatic way to update the redemption status, or the prize can be treated as redeemed straight after it was distributed, select 'Redeemed' option."
                />
                <RadioButton
                    labelVariant='body2'
                    formik={formik}
                    values={[
                        { value: 'redeemed', label: 'Redeemed' },
                        { value: 'claimed', label: 'Claimed' },
                    ]}
                    radioGroupName={'final_state'}
                />
                <TooltipText
                    dataTestId='order-in-prize-catalago-tootlip'
                    styleName='prize-tooltip-text'
                    text='Order in Prize Catalog'
                    textVariant='body1'
                    tooltip='If you want to order prizes in the catalog by their priority, specify the number here, where 1 has the highest priority.'
                />
                <InputNumberSpinner fieldName={'priority'} incrementValue={1} />
                <MultipleSelectOptions
                    formik={formik}
                    listOfOptions={[...formik.values.tags, ...tags]}
                    formikFieldName='tags'
                    subTitle='Tags'
                    inputLabel='Select Tag(s)'
                    enableAdditional={true}
                    additionalLabel='+ Add New Tag'
                    handleOpen={handleOpen}
                    modalComponent={
                        <AddToListModal
                            formikState={formik}
                            formikFieldName='tags'
                            value='tag'
                            schema={tagsValidationSchema}
                            header='+ Add New Tag'
                            modalOpenState={open}
                            listToInsertTo={tags}
                            handleClose={setOpen}
                        />
                    }
                />
            </Box>
        </Box>
    );
}
export { AdditionalDetails };
